import { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const getInitialTheme = () => {
    const saved = localStorage.getItem("theme");
    if (saved === "dark" || saved === "light") return saved;

    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
    return prefersDark ? "dark" : "light";
  };

  const [theme, setTheme] = useState(getInitialTheme); // "light" | "dark"
  const isDark = theme === "dark";

  /* ------------------ APPLY THEME ------------------ */
  // This context only selects the theme. Every colour value lives in
  // src/styles/tokens.css under :root and .dark (see DESIGN.md).
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", theme);
  }, [theme, isDark]);

  /* ------------------ SYSTEM THEME LISTENER ------------------ */
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;

    // If user explicitly chose a theme, don't override
    if (saved === "dark" || saved === "light") return;

    const onChange = (e) => setTheme(e.matches ? "dark" : "light");

    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const toggleTheme = () =>
    setTheme((t) => (t === "dark" ? "light" : "dark"));

  const value = useMemo(
    () => ({ theme, isDark, toggleTheme, setTheme }),
    [theme, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
